import React from 'react';
import { Button, Checkbox, Menu, Tooltip } from '@mantine/core';
import { AlertCircle, Calendar } from 'tabler-icons-react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import AppFlexbox from '../../common/AppFlexbox';
import AnalyticsDateSelectMenu from './AnalyticsDateSelectMenu';
import AppText from '../../common/AppText';
import AppStack from '../../common/AppStack';
import MenuContent from '../../common/MenuContent';
import AnalyticsSeasonSelectMenu from './AnalyticsSeasonSelectMenu';
import AnalyticsDivisionSelectMenu from './AnalyticsDivisionSelectMenu';
import { getDateOnly } from '../../../helpers/format';
import {
  areDatesEqual,
  getDefaultPeriodInterval
} from '../../../helpers/reportHelper';
import { COMPARE_BY_ENUM, COMPARE_BY_OPTIONS } from './analyticsConfig';
import AnalyticsFormSelectMenu from './AnalyticsFormSelectMenu';

const REPORT_PERIOD_OPTIONS = [
  {
    value: 'hour',
    label: 'Hourly'
  },
  {
    value: 'day',
    label: 'Daily'
  },
  {
    value: 'week',
    label: 'Weekly',
    isVisible: (daysBetween) => daysBetween >= 6
  },
  {
    value: 'month',
    label: 'Monthly',
    isVisible: (daysBetween) => daysBetween >= 27
  },
  {
    value: 'quarter',
    label: 'Quarterly',
    isVisible: (daysBetween) => daysBetween >= 85
  },
  {
    value: 'year',
    label: 'Yearly',
    isVisible: (daysBetween) => daysBetween >= 364
  }
];

const AnalyticsFilter = ({
  includeCompareBy,
  includeCompareDateRange,
  includePeriod,
  includeResourceSelect,
  noDateRangeEnabled,
  filter,
  optionsData,
  onChange,
  loading
}) => {
  const daysBetween = dayjs(filter.endDate).diff(
    dayjs(filter.startDate),
    'day'
  );
  const periodIntervalOptions = REPORT_PERIOD_OPTIONS.filter((op) =>
    op.isVisible ? op.isVisible(daysBetween) : true
  );

  const selectedSeason = filter.options?.find(
    (op) => op.valueName === 'season'
  );
  const selectedCompareSeason = filter.options?.find(
    (op) => op.valueName === 'compareSeason'
  );
  const selectedDivision = filter.options?.find(
    (op) => op.valueName === 'division'
  );
  const selectedFormUuid = filter.options?.find(
    (op) => op.valueName === 'formUuid'
  );
  const selectedCompareDivision = filter.options?.find(
    (op) => op.valueName === 'compareDivision'
  );

  const seasonOptions = optionsData?.seasons || [];
  const divisionOptions = optionsData?.divisions || [];

  const selectedSeasonOption = seasonOptions.find(
    (s) => s.value === selectedSeason?.value
  );
  const selectedDivisionOption = divisionOptions.find(
    (d) => d.value === selectedDivision?.value
  );

  return (
    <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
      {filter.compareBy === COMPARE_BY_ENUM.SEASON ? (
        <>
          <AnalyticsSeasonSelectMenu
            loading={loading}
            onChange={(value) => {
              onChange({
                ...filter,
                label: seasonOptions.find((s) => s.value === value)?.label,
                options: filter.options.map((o) =>
                  o.valueName === 'season' ? { ...o, value } : o
                )
              });
            }}
            seasonOptions={seasonOptions}
            value={selectedSeason?.value}
          />

          <AnalyticsSeasonSelectMenu
            isCompare
            loading={loading}
            onChange={(value) => {
              onChange({
                ...filter,
                compareLabel: seasonOptions.find((s) => s.value === value)
                  ?.label,
                options: filter.options.map((o) =>
                  o.valueName === 'compareSeason' ? { ...o, value } : o
                )
              });
            }}
            seasonOptions={seasonOptions}
            value={selectedCompareSeason?.value}
          />
        </>
      ) : filter.compareBy === COMPARE_BY_ENUM.DIVISION ? (
        <>
          <AnalyticsDivisionSelectMenu
            divisionOptions={divisionOptions}
            loading={loading}
            onChange={(value) => {
              onChange({
                ...filter,
                label: divisionOptions.find((d) => d.value === value)?.label,
                options: filter.options.map((o) =>
                  o.valueName === 'division' ? { ...o, value } : o
                )
              });
            }}
            value={selectedDivision?.value}
          />

          <AnalyticsDivisionSelectMenu
            divisionOptions={divisionOptions}
            isCompare
            loading={loading}
            onChange={(value) => {
              onChange({
                ...filter,
                compareLabel: divisionOptions.find((d) => d.value === value)
                  ?.label,
                options: filter.options.map((o) =>
                  o.valueName === 'compareDivision' ? { ...o, value } : o
                )
              });
            }}
            value={selectedCompareDivision?.value}
          />
        </>
      ) : (
        <>
          <AnalyticsDateSelectMenu
            control={
              <Button
                color="dark"
                radius={8}
                size="compact-sm"
                style={{ border: 'solid 1px #B1B1B1' }}
                variant="white"
              >
                <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
                  <Calendar size={18} />
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    {filter.noDateRange
                      ? 'No date range'
                      : filter.datePeriod?.label ??
                        (areDatesEqual(filter.startDate, filter.endDate)
                          ? dayjs(filter.startDate).format('MMM D, YYYY')
                          : `${dayjs(filter.startDate).format(
                              'MMM D, YYYY'
                            )} - ${dayjs(filter.endDate).format(
                              'MMM D, YYYY'
                            )}`)}
                  </AppText>
                </AppFlexbox>
              </Button>
            }
            datePeriod={filter.datePeriod}
            endDate={filter.endDate}
            noDateRange={filter.noDateRange}
            noDateRangeEnabled={noDateRangeEnabled}
            onChange={({ datePeriod, startDate, endDate, noDateRange }) => {
              const {
                startDate: compareStartDate,
                endDate: compareEndDate
              } = filter.compareDatePeriod?.getDateRange(
                datePeriod,
                startDate,
                endDate
              ) ?? {
                startDate: filter.compareStartDate,
                endDate: filter.compareEndDate
              };

              const newStart = getDateOnly(startDate);
              const newEnd = getDateOnly(endDate);
              onChange({
                ...filter,
                datePeriod,
                startDate: newStart,
                endDate: newEnd,
                noDateRange,
                compareStartDate: getDateOnly(compareStartDate),
                compareEndDate: getDateOnly(compareEndDate),
                periodInterval: getDefaultPeriodInterval(newStart, newEnd)
              });
            }}
            startDate={filter.startDate}
          />

          {includeCompareDateRange && !filter.noDateRange && (
            <AnalyticsDateSelectMenu
              compareDatePeriod={filter.compareDatePeriod}
              compareEndDate={filter.compareEndDate}
              compareStartDate={filter.compareStartDate}
              control={
                <Button
                  color="dark"
                  radius={8}
                  size="compact-sm"
                  style={{ border: 'solid 1px #B1B1B1' }}
                  variant="white"
                >
                  <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      <span style={{ color: '#999' }}>Compared to:</span>{' '}
                      {filter.compareDatePeriod
                        ? filter.compareDatePeriod.label
                        : areDatesEqual(
                            filter.compareStartDate,
                            filter.compareEndDate
                          )
                        ? dayjs(filter.compareStartDate).format('MMM D, YYYY')
                        : `${dayjs(filter.compareStartDate).format(
                            'MMM D, YYYY'
                          )} - ${dayjs(filter.compareEndDate).format(
                            'MMM D, YYYY'
                          )}`}
                    </AppText>
                  </AppFlexbox>
                </Button>
              }
              datePeriod={filter.datePeriod}
              endDate={filter.endDate}
              isCompareDateRange
              onChange={({ datePeriod, startDate, endDate }) => {
                onChange({
                  ...filter,
                  compareDatePeriod: datePeriod,
                  compareStartDate: getDateOnly(startDate),
                  compareEndDate: getDateOnly(endDate)
                });
              }}
              startDate={filter.startDate}
            />
          )}
        </>
      )}

      {includeCompareBy && (
        <MenuContent
          closeOnClickOutside
          closeOnItemClick
          control={
            <Button
              color="dark"
              radius={8}
              size="compact-sm"
              style={{ border: 'solid 1px #B1B1B1' }}
              variant="white"
            >
              <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  <span style={{ color: '#999' }}>Compare by:</span>{' '}
                  {
                    COMPARE_BY_OPTIONS.find((g) => g.value === filter.compareBy)
                      ?.label
                  }
                </AppText>
              </AppFlexbox>
            </Button>
          }
          position="bottom-start"
          radius="md"
          shadow="xl"
          styles={{ dropdown: { padding: 0 } }}
          width="unset"
        >
          <AppStack style={{ gap: 0, flex: 1 }}>
            {COMPARE_BY_OPTIONS.map((g) => (
              <Menu.Item
                key={g.value}
                onClick={(e) => {
                  e.preventDefault();
                  onChange({
                    ...filter,
                    compareBy: g.value
                  });
                }}
              >
                <AppFlexbox style={{ alignItems: 'center', gap: 12 }}>
                  <Checkbox
                    checked={filter.compareBy === g.value}
                    onChange={() => {}}
                  />
                  <AppText style={{ fontSize: 14 }}>{g.label}</AppText>
                </AppFlexbox>
              </Menu.Item>
            ))}
          </AppStack>
        </MenuContent>
      )}

      {includePeriod && (
        <MenuContent
          closeOnClickOutside
          closeOnItemClick
          control={
            <Button
              color="dark"
              radius={8}
              size="compact-sm"
              style={{ border: 'solid 1px #B1B1B1' }}
              variant="white"
            >
              <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  {REPORT_PERIOD_OPTIONS.find(
                    (op) => op.value === filter.periodInterval
                  )?.label ?? 'Period'}{' '}
                </AppText>
              </AppFlexbox>
            </Button>
          }
          position="bottom-start"
          radius="md"
          shadow="xl"
          styles={{ dropdown: { padding: 0 } }}
          width="unset"
        >
          <AppStack style={{ gap: 0, flex: 1 }}>
            {periodIntervalOptions.map((op) => (
              <Menu.Item
                key={op.value}
                onClick={(e) => {
                  e.preventDefault();
                  onChange({
                    ...filter,
                    periodInterval: op.value
                  });
                }}
              >
                <AppFlexbox style={{ alignItems: 'center', gap: 12 }}>
                  <Checkbox
                    checked={filter.periodInterval === op.value}
                    onChange={() => {}}
                  />
                  <AppText style={{ fontSize: 14 }}>{op.label}</AppText>
                </AppFlexbox>
              </Menu.Item>
            ))}
          </AppStack>
        </MenuContent>
      )}

      {includeResourceSelect && (
        <>
          <AnalyticsSeasonSelectMenu
            allowSelectAll
            loading={loading}
            onChange={(value) => {
              onChange({
                ...filter,
                label: seasonOptions.find((s) => s.value === value)?.label,
                options: value
                  ? [
                      ...filter.options.filter(
                        (o) =>
                          o.valueName !== 'season' && o.valueName !== 'division'
                      ),
                      { valueName: 'season', value }
                    ]
                  : filter.options.filter(
                      (o) =>
                        o.valueName !== 'season' && o.valueName !== 'division'
                    )
              });
            }}
            seasonOptions={seasonOptions}
            value={selectedSeason?.value}
          />

          <AnalyticsDivisionSelectMenu
            allowSelectAll
            defaultSeasonName={selectedSeasonOption?.label}
            divisionOptions={divisionOptions}
            loading={loading}
            onChange={(value) => {
              const divisionOption = divisionOptions.find(
                (d) => d.value === value
              );

              onChange({
                ...filter,
                label: divisionOption?.label,
                options: divisionOption
                  ? [
                      ...filter.options.filter(
                        (o) =>
                          o.valueName !== 'division' && o.valueName !== 'season'
                      ),
                      { valueName: 'division', value },
                      {
                        valueName: 'season',
                        value: divisionOption.seasonValue
                      }
                    ]
                  : filter.options.filter((o) => o.valueName !== 'division')
              });
            }}
            value={selectedDivision?.value}
          />

          <AnalyticsFormSelectMenu
            allowSelectAll
            defaultDivisionValue={selectedDivisionOption?.value}
            defaultSeasonValue={selectedSeasonOption?.value}
            loading={loading}
            onChange={(value) => {
              onChange({
                ...filter,
                label: optionsData.forms.find((f) => f.value === value)?.label,
                options: value
                  ? [
                      ...filter.options.filter(
                        (o) => o.valueName !== 'formUuid'
                      ),
                      { valueName: 'formUuid', value }
                    ]
                  : filter.options.filter((o) => o.valueName !== 'formUuid')
              });
            }}
            optionsData={optionsData}
            value={selectedFormUuid?.value}
          />
        </>
      )}

      {filter.compareBy !== COMPARE_BY_ENUM.SEASON &&
        filter.compareBy !== COMPARE_BY_ENUM.DIVISION && (
          <Tooltip
            label={
              <AppStack>
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  The data is grouped based on the registration date rather than
                  the transaction date.
                </AppText>
                <AppText style={{ fontSize: 14 }}>
                  As a result, new transactions will be reflected on the date
                  when the registration was placed.
                </AppText>
              </AppStack>
            }
            multiline
            w={250}
            withArrow
          >
            <AppFlexbox>
              <AlertCircle color="dodgerblue" size={22} />
            </AppFlexbox>
          </Tooltip>
        )}
    </AppFlexbox>
  );
};

AnalyticsFilter.propTypes = {
  filter: PropTypes.object,
  includeCompareBy: PropTypes.bool,
  includeCompareDateRange: PropTypes.bool,
  includePeriod: PropTypes.bool,
  includeResourceSelect: PropTypes.bool,
  loading: PropTypes.bool,
  noDateRangeEnabled: PropTypes.bool,
  onChange: PropTypes.func,
  optionsData: PropTypes.object
};

export default AnalyticsFilter;
